import React, { useState, useMemo } from "react";
import SolicitudPermiso from "./SolicitudPermiso";
import FormatoTraslado from "./FormatoTraslado";
import FormatoCesantias from "./FormatoCesantias"; 
import FormatoVacaciones from "./FormatoVacaciones";
import FormatoRequisicionPersonal from "./FormatoRequisicionPersonal"; 

export default function ModuloPersonalMain({ usuario, onBackToMenu }) {
  const [vistaActiva, setVistaActiva] = useState("menu"); 

  const permisosABAC = useMemo(() => {
    try {
      const parsed = typeof usuario?.permisos_avanzados === 'string' 
        ? JSON.parse(usuario.permisos_avanzados) 
        : (usuario?.permisos_avanzados || {});
      return parsed.micros || parsed; 
    } catch (e) { 
      return {}; 
    }
  }, [usuario]);

  const cargoUpper = String(usuario?.cargo || "").toUpperCase().trim();
  const esAdminGlobal = cargoUpper === "SUPER_ADMIN";
  
  const checkPermiso = (nombreSubmodulo) => {
    if (esAdminGlobal) return true;
    return !!permisosABAC[nombreSubmodulo] && permisosABAC[nombreSubmodulo] !== false;
  };

  const verPermiso = checkPermiso('FO-TH-96: Permiso');
  const verTraslado = checkPermiso('FO-TH-97: Traslado');
  const verCesantias = checkPermiso('FO-TH-98: Cesantías');
  const verVacaciones = checkPermiso('FOR-TH-99: Vacaciones');
  const verRequisicion = checkPermiso('FT-TH-10: Requisición');

  if (vistaActiva === "permiso") {
    return <SolicitudPermiso usuario={usuario} filtroABAC={permisosABAC['FO-TH-96: Permiso']} onBackToMenu={() => setVistaActiva("menu")} />;
  }

  if (vistaActiva === "traslado") {
    return <FormatoTraslado usuario={usuario} filtroABAC={permisosABAC['FO-TH-97: Traslado']} onBackToMenu={() => setVistaActiva("menu")} />;
  }

  if (vistaActiva === "cesantias") {
    return <FormatoCesantias usuario={usuario} filtroABAC={permisosABAC['FO-TH-98: Cesantías']} onBackToMenu={() => setVistaActiva("menu")} />;
  }

  if (vistaActiva === "vacaciones") {
    return <FormatoVacaciones usuario={usuario} filtroABAC={permisosABAC['FOR-TH-99: Vacaciones']} onBackToMenu={() => setVistaActiva("menu")} />;
  }

  if (vistaActiva === "requisicion") {
    return <FormatoRequisicionPersonal usuario={usuario} filtroABAC={permisosABAC['FT-TH-10: Requisición']} onBackToMenu={() => setVistaActiva("menu")} />;
  }

  const sinPermisosModulo = !verPermiso && !verTraslado && !verCesantias && !verVacaciones && !verRequisicion;

  return (
    <div className="bg-slate-50/80 backdrop-blur-3xl p-6 sm:p-10 rounded-[32px] shadow-sm border border-white w-full max-w-5xl mx-auto animate-fade-in-up">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 pb-6 border-b border-gray-200 gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">📂 Gestión y Trámites de Personal</h2>
          <p className="text-sm text-gray-500 font-medium mt-1">Seleccione el formato o solicitud que desea diligenciar.</p>
        </div>
        <button onClick={onBackToMenu} className="px-5 py-2.5 text-xs font-bold bg-white hover:bg-gray-50 text-gray-800 rounded-full border border-gray-200 cursor-pointer shadow-sm">
          Volver al Menú Principal
        </button>
      </div>

      {sinPermisosModulo ? (
        <div className="p-10 text-center bg-white/60 backdrop-blur-md border border-gray-100 rounded-3xl shadow-sm">
          <span className="text-4xl block mb-4">🛡️</span>
          <h3 className="text-lg font-black text-gray-800">Módulo Bloqueado por Control de Accesos</h3>
          <p className="text-xs text-gray-500 font-medium mt-2">No tienes formularios de Gestión Humana asignados en tu matriz de privilegios.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6">
          
          {verPermiso && (
            <div onClick={() => setVistaActiva("permiso")} className="bg-white/85 backdrop-blur-xl p-6 rounded-[24px] border border-blue-100 shadow-[0_4px_20px_rgb(0,0,0,0.03)] hover:shadow-lg transition-all cursor-pointer flex flex-col justify-between group hover:-translate-y-1">
              <div>
                <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center text-2xl mb-4 group-hover:scale-110 transition-transform shadow-inner">📝</div>
                <h3 className="text-lg font-extrabold text-gray-900 mb-1">FO-TH-96: Permiso</h3>
                <p className="text-xs text-gray-500 leading-relaxed">Radique solicitudes de permisos laborales por días u horas ante su jefatura y Talento Humano.</p>
              </div>
              <div className="mt-6 flex items-center justify-between">
                <span className="text-[11px] font-bold px-3 py-1 rounded-full text-blue-700 bg-blue-50">Formulario Activo</span>
                <span className="text-xs font-bold text-blue-600 group-hover:translate-x-1 transition-transform">Ingresar</span>
              </div>
            </div>
          )}

          {verTraslado && (
            <div onClick={() => setVistaActiva("traslado")} className="bg-white/85 backdrop-blur-xl p-6 rounded-[24px] border border-blue-100 shadow-[0_4px_20px_rgb(0,0,0,0.03)] hover:shadow-lg transition-all cursor-pointer flex flex-col justify-between group hover:-translate-y-1">
              <div>
                <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center text-2xl mb-4 group-hover:scale-110 transition-transform shadow-inner">🔄</div>
                <h3 className="text-lg font-extrabold text-gray-900 mb-1">FO-TH-97: Traslado</h3>
                <p className="text-xs text-gray-500 leading-relaxed">Gestión de traslados entre centros de trabajo y reporte de novedades de nómina.</p>
              </div>
              <div className="mt-6 flex items-center justify-between">
                <span className="text-[11px] font-bold px-3 py-1 rounded-full text-blue-700 bg-blue-50">Formulario Activo</span>
                <span className="text-xs font-bold text-blue-600 group-hover:translate-x-1 transition-transform">Ingresar</span>
              </div>
            </div>
          )}

          {verCesantias && (
            <div onClick={() => setVistaActiva("cesantias")} className="bg-white/85 backdrop-blur-xl p-6 rounded-[24px] border border-blue-100 shadow-[0_4px_20px_rgb(0,0,0,0.03)] hover:shadow-lg transition-all cursor-pointer flex flex-col justify-between group hover:-translate-y-1">
              <div>
                <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center text-2xl mb-4 group-hover:scale-110 transition-transform shadow-inner">💰</div>
                <h3 className="text-lg font-extrabold text-gray-900 mb-1">FO-TH-98: Cesantías</h3>
                <p className="text-xs text-gray-500 leading-relaxed">Solicitud de retiro parcial o definitivo de cesantías para vivienda, educación u otros fines.</p>
              </div>
              <div className="mt-6 flex items-center justify-between">
                <span className="text-[11px] font-bold px-3 py-1 rounded-full text-blue-700 bg-blue-50">Formulario Activo</span>
                <span className="text-xs font-bold text-blue-600 group-hover:translate-x-1 transition-transform">Ingresar</span>
              </div>
            </div>
          )}

          {verVacaciones && (
            <div onClick={() => setVistaActiva("vacaciones")} className="bg-white/85 backdrop-blur-xl p-6 rounded-[24px] border border-blue-100 shadow-[0_4px_20px_rgb(0,0,0,0.03)] hover:shadow-lg transition-all cursor-pointer flex flex-col justify-between group hover:-translate-y-1">
              <div>
                <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center text-2xl mb-4 group-hover:scale-110 transition-transform shadow-inner">🏖️</div>
                <h3 className="text-lg font-extrabold text-gray-900 mb-1">FOR-TH-99: Vacaciones</h3>
                <p className="text-xs text-gray-500 leading-relaxed">Solicitud y programación de vacaciones, periodos causados y días hábiles a disfrutar.</p>
              </div>
              <div className="mt-6 flex items-center justify-between">
                <span className="text-[11px] font-bold px-3 py-1 rounded-full text-blue-700 bg-blue-50">Formulario Activo</span>
                <span className="text-xs font-bold text-blue-600 group-hover:translate-x-1 transition-transform">Ingresar</span>
              </div>
            </div>
          )}

          {verRequisicion && (
            <div onClick={() => setVistaActiva("requisicion")} className="bg-white/85 backdrop-blur-xl p-6 rounded-[24px] border border-blue-100 shadow-[0_4px_20px_rgb(0,0,0,0.03)] hover:shadow-lg transition-all cursor-pointer flex flex-col justify-between group hover:-translate-y-1">
              <div>
                <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center text-2xl mb-4 group-hover:scale-110 transition-transform shadow-inner">👤</div>
                <h3 className="text-lg font-extrabold text-gray-900 mb-1">FT-TH-10: Requisición</h3>
                <p className="text-xs text-gray-500 leading-relaxed">Solicitud formal de reemplazo o contratación por ampliación de planta de personal.</p>
              </div>
              <div className="mt-6 flex items-center justify-between">
                <span className="text-[11px] font-bold px-3 py-1 rounded-full text-blue-700 bg-blue-50">Formulario Activo</span>
                <span className="text-xs font-bold text-blue-600 group-hover:translate-x-1 transition-transform">Ingresar</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}